import React from 'react';
import { ICapsuleData } from '@/pages/strategic/components/CapsuleBar/type';
import { getTableCapsuleConfig } from '@sg/utils/business';
import SpecialTableBar from '@sg/components/CapsuleBar/SpecialTableBar';
import SvgIcon from '@/pages/home/components/SvgIcon';
import Narrow from '@sg/assests/images/Narrow.svg';
import { toMillions, getYearPrefix, getMonthYearPrefix } from '@sg/utils/common';
import { IntlShape } from 'react-intl';
import { localizeLabel } from '@sg/utils/translate';
import ExpandCollapseButton from '@sg/components/ExpandCollapseButton';
import EditableField from '@sg/components/EditableField';
import { rendTitle, renderNewlineToBr } from '@sg/utils/commonPageMethod';
import { createBtnPoint, SGBuriedBtnPoint, PortfolioOverviewTab } from '@/pages/strategic/utils/buriedPoint';
import { getPhaseStudioIndent } from './utils';

const getVsFieldConfig = (viewMode?: string) => {
  const leftField = viewMode === 'month' ? '25A' : '25F';
  const rightField = '25B';
  return {
    leftField,
    rightField,
    label: `${leftField} VS ${rightField}`,
  };
};

export const PhaseColums = (
  maxVsVallue: number,
  end_date: string,
  formatMessage: IntlShape['formatMessage'],
  isAllExpanded?: boolean,
  onToggleExpandAll?: () => void,
  viewMode?: string,
) => {
  const yearPrefix = viewMode === 'month'
    ? getMonthYearPrefix(end_date)
    : getYearPrefix(end_date);
  const { leftField, rightField, label: vsLabel } = getVsFieldConfig(viewMode);

  return [
    {
      header: (
        <div>
          {onToggleExpandAll ? (
            <ExpandCollapseButton
              isExpanded={!!isAllExpanded}
              onClick={() => onToggleExpandAll()}
              onTrackToggle={(nextExpanded) => {
                const btnPoint = createBtnPoint(
                  PortfolioOverviewTab,
                  'phaseTableCollapse',
                  nextExpanded ? 'Phase Table Expand All' : 'Phase Table Collapse All',
                );
                if (btnPoint) {
                  SGBuriedBtnPoint(btnPoint);
                }
              }}
              title={formatMessage({ id: 'dbs_table_phase_studio' })}
            />
          ) : (
            <span style={{ fontWeight: 700 }}>
              {formatMessage({ id: 'dbs_table_phase_studio' })}
            </span>
          )}
        </div>
      ),
      dataIndex: 'Phase',
      key: 'Phase',
      width: 140,
      render: (cvm: any) => <span>
        <span style={{ fontWeight: 700, marginLeft: getPhaseStudioIndent(cvm) }}>
          {/* translate happen here */}
          {localizeLabel(cvm.Phase, formatMessage)}
        </span>
        {cvm.Phase && cvm.Studio && '/'}
        <span style={{ fontWeight: 400 }}>{cvm.Studio}</span>
      </span>,
    },
    {
      header: rendTitle({ title: `${yearPrefix}A`, tips: '(M$)', titleSize: '14px', tipSize: '12px' }),
      dataIndex: '25A',
      key: '25A',
      width: 70,
      render: (cvm: any) => (
        <div style={{ textAlign: 'right', width: '57px', textWrap: 'nowrap' }}>
          {toMillions(cvm?.['25A'])}
        </div>
      ),
    },
    {
      header: rendTitle({ title: `${yearPrefix}F`, tips: '(M$)', titleSize: '14px', tipSize: '12px' }),
      dataIndex: '25F',
      key: '25F',
      width: 70,
      render: (cvm: any) => (
        <div style={{ textAlign: 'right', width: '57px', textWrap: 'nowrap' }}>
          {toMillions(cvm?.['25F'])}
        </div>
      ),
    },
    {
      header: rendTitle({ title: `${yearPrefix}B`, tips: '(M$)', titleSize: '14px', tipSize: '12px' }),
      dataIndex: '25B',
      key: '25B',
      width: 70,
      render: (cvm: any) => (
        <div style={{ textAlign: 'right', width: '57px', textWrap: 'nowrap' }}>
          {toMillions(cvm?.['25B'])}
        </div>
      ),
    },
    {
      header: <div className='dbs-table-25FVS25B'>
        {rendTitle({ title: `${yearPrefix}${leftField.slice(2)} VS ${yearPrefix}${rightField.slice(2)}`, tips: ' (M$)', titleSize: '14px', tipSize: '12px' })}
      </div>,
      dataIndex: vsLabel,
      key: vsLabel,
      width: 100,
      render: (cvm: any) => {
        try {
          const dValue = Number((cvm?.[leftField] - cvm?.[rightField])?.toFixed(2)) || 0;
          if (dValue >= 0) {
            return null;
          }
          const capsuleValueStr = toMillions(dValue);
          const capsuleMaxValueStr = toMillions(maxVsVallue);
          const capsuleValue = Number(capsuleValueStr);
          const capsuleMaxValue = Number(capsuleMaxValueStr);
          if (Number.isNaN(capsuleValue) || Number.isNaN(capsuleMaxValue)) {
            return null;
          }
          const newArr: ICapsuleData[] = getTableCapsuleConfig({ value: capsuleValue, maxValue: capsuleMaxValue });
          return <div className='dbs-table-capsule dbs-table-capsule-negative'>
            <SpecialTableBar data={newArr} height={20} coefficient={1.5} />
          </div>;
        } catch (error) {
          console.error(error);
        }
      },
    },
    {
      header: '',
      dataIndex: `${vsLabel}2`,
      key: `${vsLabel}2`,
      width: 100,
      render: (cvm: any) => {
        try {
          const dValue = Number((cvm?.[leftField] - cvm?.[rightField])?.toFixed(2)) || 0;
          if (dValue <= 0) {
            return null;
          }
          const capsuleValueStr = toMillions(dValue);
          const capsuleMaxValueStr = toMillions(maxVsVallue);
          const capsuleValue = Number(capsuleValueStr);
          const capsuleMaxValue = Number(capsuleMaxValueStr);
          if (Number.isNaN(capsuleValue) || Number.isNaN(capsuleMaxValue)) {
            return null;
          }
          const newArr: ICapsuleData[] = getTableCapsuleConfig({ value: capsuleValue, maxValue: capsuleMaxValue });
          return <div
            className='dbs-table-capsule dbs-table-capsule-positive'
            style={{ marginLeft: dValue === 0 ? '0' : '-20px' }}
          >
            <SpecialTableBar data={newArr} height={20} coefficient={2} />
          </div>;
        } catch (error) {
          console.error(error);
        }
      },
    },
    {
      header: formatMessage({ id: 'dbs_opportunities_risks' }),
      dataIndex: 'Opportunities & Risks',
      key: 'Opportunities & Risks',
      width: 140,
      render: (cvm: any) => {
        const value = cvm?.['Opportunities & Risks'];
        const diff = cvm['25F'] - cvm['25B'];
        const showIcon = Boolean(value);
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            {showIcon && (
              <span style={{ fontSize: '12px', display: 'inline-flex', alignItems: 'center' }}>
                {(diff > 0 ?
                  <SvgIcon style={{ color: '#69D19C', position: 'relative', top: '-1px' }} type={'cross'} /> :
                  (diff === 0 ? '' : <img width="12px" src={Narrow} alt="" style={{ position: 'relative', top: '1px' }} />))}
              </span>
            )}
            {/* <span style={{ flex: 1 }}>{renderRichText(value)}</span> */}
            <span>
              {renderNewlineToBr(cvm?.['Opportunities & Risks'])}
            </span>
          </div>
        );
      },
    },
    {
      header: formatMessage({ id: 'dbs_action_note' }),
      dataIndex: 'Action & Note',
      key: 'Action & Note',
      width: 100,
      // render: (cvm: any) => <div>{renderRichText(cvm?.['Action & Note'])}</div>
      render: (cvm: any) => <div>{renderNewlineToBr(cvm?.['Action & Note'])}</div>,
    },
  ];
};

export const EditPhaseEditColums = (
  maxVsVallue: number,
  end_date: string,
  formatMessage: IntlShape['formatMessage'],
  viewMode?: string,
) => {
  const yearPrefix = viewMode === 'month'
    ? getMonthYearPrefix(end_date)
    : getYearPrefix(end_date);
  const { leftField, rightField, label: vsLabel } = getVsFieldConfig(viewMode);

  return [
    {
      header: <div style={{ fontWeight: 700 }}>{formatMessage({ id: 'dbs_table_phase_studio' })}</div>,
      dataIndex: 'Phase',
      key: 'Phase',
      width: 140,
      render: (cvm: any) => (
        <span>
          <span style={{ fontWeight: 700 }}>
            {/* PhaseCell (rename/delete) disabled — read-only label for all rows */}
            <span>{localizeLabel(cvm.Phase, formatMessage) || cvm.Phase || ''}</span>
          </span>
          {cvm.Phase && cvm.Studio && '/'}
          <span style={{ fontWeight: 400 }}>{cvm.Studio}</span>
        </span>
      ),
    },
    {
      header: rendTitle({ title: `${yearPrefix}A`, tips: '(M$)', titleSize: '14px', tipSize: '12px' }),
      dataIndex: '25A',
      key: '25A',
      width: 70,
      render: (cvm: any) => (
        <div style={{ textAlign: 'right', width: '57px', textWrap: 'nowrap' }}>
          {toMillions(cvm?.['25A'])}
        </div>
      ),
    },
    {
      header: rendTitle({ title: `${yearPrefix}F`, tips: '(M$)', titleSize: '14px', tipSize: '12px' }),
      dataIndex: '25F',
      key: '25F',
      width: 70,
      render: (cvm: any) => (
        <div style={{ textAlign: 'right', width: '57px', textWrap: 'nowrap' }}>
          {toMillions(cvm?.['25F'])}
        </div>
      ),
    },
    {
      header: rendTitle({ title: `${yearPrefix}B`, tips: '(M$)', titleSize: '14px', tipSize: '12px' }),
      dataIndex: '25B',
      key: '25B',
      width: 70,
      render: (cvm: any) => (
        <div style={{ textAlign: 'right', width: '57px', textWrap: 'nowrap' }}>
          {toMillions(cvm?.['25B'])}
        </div>
      ),
    },
    {
      header: <div className='dbs-table-25FVS25B'>
        {rendTitle({ title: `${yearPrefix}${leftField.slice(2)} VS ${yearPrefix}${rightField.slice(2)}`, tips: ' (M$)', titleSize: '14px', tipSize: '12px' })}
      </div>,
      dataIndex: vsLabel,
      key: vsLabel,
      width: 100,
      render: (cvm: any) => {
        try {
          const dValue = Number((cvm?.[leftField] - cvm?.[rightField])?.toFixed(2)) || 0;
          if (dValue >= 0) {
            return null;
          }
          const capsuleValueStr = toMillions(dValue);
          const capsuleMaxValueStr = toMillions(maxVsVallue);
          const capsuleValue = Number(capsuleValueStr);
          const capsuleMaxValue = Number(capsuleMaxValueStr);
          if (Number.isNaN(capsuleValue) || Number.isNaN(capsuleMaxValue)) {
            return null;
          }
          const newArr: ICapsuleData[] = getTableCapsuleConfig({ value: capsuleValue, maxValue: capsuleMaxValue });
          return <div className='dbs-table-capsule dbs-table-capsule-negative'>
            <SpecialTableBar data={newArr} height={20} coefficient={1.5} />
          </div>;
        } catch (error) {
          console.error(error);
        }
      },
    },
    {
      header: '',
      dataIndex: `${vsLabel}2`,
      key: `${vsLabel}2`,
      width: 100,
      render: (cvm: any) => {
        try {
          const dValue = Number((cvm?.[leftField] - cvm?.[rightField])?.toFixed(2)) || 0;
          if (dValue <= 0) {
            return null;
          }
          const capsuleValueStr = toMillions(dValue);
          const capsuleMaxValueStr = toMillions(maxVsVallue);
          const capsuleValue = Number(capsuleValueStr);
          const capsuleMaxValue = Number(capsuleMaxValueStr);
          if (Number.isNaN(capsuleValue) || Number.isNaN(capsuleMaxValue)) {
            return null;
          }
          const newArr: ICapsuleData[] = getTableCapsuleConfig({ value: capsuleValue, maxValue: capsuleMaxValue });
          return <div
            className='dbs-table-capsule dbs-table-capsule-positive'
            style={{ marginLeft: dValue === 0 ? '0' : '-20px' }}
          >
            <SpecialTableBar data={newArr} height={20} coefficient={2} />
          </div>;
        } catch (error) {
          console.error(error);
        }
      },
    },
    {
      header: formatMessage({ id: 'dbs_opportunities_risks' }),
      dataIndex: 'Opportunities & Risks',
      key: 'Opportunities & Risks',
      width: 140,
      render: (cvm: any, rowKey: string, recordIndex: number, column: any, columnIndex: number, editData: any, stableUpdater: (value: any) => void) => {
        const onChange = (value: any) => {
          stableUpdater(value);
        };
        return (
          <EditableField initialData={cvm} renderStyle={column.key} onChange={onChange} />
        );
      },
    },
    {
      header: formatMessage({ id: 'dbs_action_note' }),
      dataIndex: 'Action & Note',
      key: 'Action & Note',
      width: 100,
      render: (cvm: any, rowKey: string, recordIndex: number, column: any, columnIndex: number, editData: any, stableUpdater: (value: any) => void) => {
        const onChange = (value: any) => {
          stableUpdater(value);
        };
        return (
          <EditableField initialData={cvm} renderStyle={column.key} onChange={onChange} />
        );
      },
    },
  ];
};
